import React from "react";
import { Button, FormFeedback, FormGroup, Input, Label } from "reactstrap";
import CommonSelect from "../components/Common/Select";
import InputMask from "react-input-mask"; // Import InputMask for masking
import { AudioRecorder } from 'react-audio-voice-recorder';

const generateFormUI = (
  formData,
  formik,
  onChange,
  disabled,
  assignedUser,
  setSelectedUser,
  isEditMode
) => {
  // For handling audio recordings
  const addAudioElement = (blob, fieldId) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement("audio");
    audio.src = url;
    audio.controls = true;
    
    const audioContainer = document.getElementById(`audio-preview-${fieldId}`);
    if (audioContainer) {
      audioContainer.innerHTML = '';
      audioContainer.appendChild(audio);
    }
    
    // Convert blob to File object with metadata
    const now = new Date();
    const fileName = `recording_${now.getTime()}.webm`;
    
    // Create a File object from the Blob with additional metadata
    const file = new File([blob], fileName, {
      type: blob.type,
      lastModified: now.getTime()
    });
    
    const existingRecordings = formik.values[fieldId] || [];
    const updatedRecordings = [...existingRecordings, file]; // Store as File objects
    
    formik.setFieldValue(fieldId, updatedRecordings);
    
    if (onChange) {
      const updatedValues = {
        ...formik.values,
        [fieldId]: updatedRecordings,
      };
      onChange(updatedValues);
    }
  };

  const getFormattedDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Ensure we're working with a number timestamp
    const timestampNum = Number(timestamp);
    
    // Create a date object from the timestamp in local timezone
    const date = new Date(timestampNum);
    const istDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));

    const year = istDate.getFullYear();
    const month = (istDate.getMonth() + 1).toString().padStart(2, '0');
    const day = istDate.getDate().toString().padStart(2, '0');
  
    return `${year}-${month}-${day}`;
  };
  
  const getFormattedTime = (timestamp) => {
    if (!timestamp) return '';
    
    // Ensure we're working with a number timestamp
    const timestampNum = Number(timestamp);
    
    // Create a date object from the timestamp in local timezone
    const date = new Date(timestampNum);
    
    // Extract hours and minutes, pad with zero if needed
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  };

  return formData?.map((field, index) => (
    <FormGroup
      key={index}
      className={`col-${field.width === "full" ? "12" : "6"}`}
    >
<Label for={field.inputName}>
      {field.label} {field?.required && <span style={{ color: "red" }}>*</span>}
    </Label>
      {field.inputName.includes("assigned") ? (
        <CommonSelect
          value={assignedUser}
          onChange={(selectedOption) => {
            setSelectedUser(selectedOption);
            if (onChange) {
              const updatedValues = {
                ...formik.values,
                [field.inputName]: selectedOption,
              };
              onChange(updatedValues);
            }
          }}
          placeholder="Search and select user..."
        />
      ) : field.type === "select" ? (
        <Input
          disabled={disabled}
          type="select"
          id={field.inputName}
          name={field.inputName}
          value={formik.values[field.inputName]}
          onChange={(e) => {
            formik.handleChange(e);
            if (onChange) {
              const updatedValues = {
                ...formik.values,
                [e.target.name]: e.target.value,
              };
              onChange(updatedValues);
            }
          }}
          onBlur={formik.handleBlur}
          invalid={
            formik.touched[field.inputName] &&
            Boolean(formik.errors[field.inputName])
          }
        >
          <option value="">Select an option</option>
          {field.options?.map((option, idx) => (
            <option key={idx} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      ) : field.type === "file" ? (
        <>
          <Input
            disabled={disabled}
            type="file"
            id={field.id}
            name={field.id}
            accept={field.acceptedTypes}
            multiple={field.multiple !== false}
            onChange={(event) => {
              const newFiles = Array.from(event.target.files); // Get new files
              const existingFiles = formik.values[field.id] || []; // Existing files
      
              // Merge existing files with new files
              const updatedFiles = [...existingFiles, ...newFiles];
              formik.setFieldValue(field.id, updatedFiles); // Update formik value
              if (onChange) {
                const updatedValues = {
                  ...formik.values,
                  [field.id]: updatedFiles,
                };
                onChange(updatedValues);
              }
            }}
            onBlur={formik.handleBlur}
            invalid={
              formik.touched[field.id] &&
              Boolean(formik.errors[field.id])
            }
          />
          
          {formik.values[field.id]?.length > 0 && (
            <div className="file-list mt-3">
              <Label>Uploaded Files:</Label>
              <table className="table table-sm table-bordered mt-1">
                
                <tbody>
                  {formik.values[field.id].map((file, index) => (
                    <tr key={index}>
                      <td>
                        {file.name || `File ${index + 1}`}
                      </td>                     
                      <td>
                        {typeof file === 'string' ? (
                          // For already uploaded files (shown as paths)
                          <a 
                            href={`${process.env.REACT_APP_ASSETURL}/${file}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary mr-2"
                          >
                            View
                          </a>
                        ) : (
                          // For newly added files (shown as File objects)
                          <a 
                            href={URL.createObjectURL(file)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-primary mr-2"
                          >
                            Preview
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            const updatedFiles = [...formik.values[field.id]];
                            updatedFiles.splice(index, 1);
                            formik.setFieldValue(field.id, updatedFiles);
                            if (onChange) {
                              const updatedValues = {
                                ...formik.values,
                                [field.id]: updatedFiles,
                              };
                              onChange(updatedValues);
                            }
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : field.type === "audio" ? (
        <>
          <div   className={`audio-recorder-container ${isEditMode || disabled ? "disabled" : ""}`} 
>
            <AudioRecorder 
              onRecordingComplete={(blob) => {addAudioElement(blob, field.id || field.inputName)}}
              audioTrackConstraints={{
                noiseSuppression: true,
                echoCancellation: true,
              }}
              disabled={isEditMode || disabled} 
            />
          </div>
          
          {formik.values[field?.id || field?.inputName]?.length > 0 && (
  <div className="mt-3">
    <ul className="mt-1">
    {formik.values[field?.id || field?.inputName]?.length > 0 && (
  <div className="mt-3 p-2 shadow-md bg-white rounded-md">
    <table className="mt-2 w-full border-collapse text-sm">
      <tbody>
        {formik?.values[field.id || field.inputName]?.map((recording, index) => (
          <tr key={index} className="text-left">
            <td className="px-2 py-1 w-8 text-gray-600">{index + 1}.</td>
            <td className="px-2 py-1">
              <audio controls src={typeof recording === "string" ?`${process.env.REACT_APP_ASSETURL}/${recording}`  : URL.createObjectURL(recording)} className="w-40" />
            </td>
            {/* <td className="px-2 py-1 text-right">
              <Button 
              color="primary"
                className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600"
                onClick={() => {
                  const updatedRecordings = [...formik.values[field.id || field.inputName]];
                  updatedRecordings.splice(index, 1);
                  formik.setFieldValue(field.id || field.inputName, updatedRecordings);
                  if (onChange) {
                    const updatedValues = {
                      ...formik.values,
                      [field.id || field.inputName]: updatedRecordings,
                    };
                    onChange(updatedValues);
                  }
                }}
              >
                Remove
              </Button>
            </td> */}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}
    </ul>
  </div>
)}

        
        </>
      ) : field.type === "phoneNumber" || field.inputName === "contactNumber" || field.id === "contactNumber" ? (
        <InputMask
        mask="999-999-9999"
        disabled={disabled}
        value={formik.values[field.inputName || field.id]}
        onChange={(e) => {
          formik.handleChange(e);
          if (onChange) {
            const updatedValues = {
              ...formik.values,
              [e.target.name]: e.target.value,
            };
            onChange(updatedValues);
          }
        }}
        onBlur={(e) => {
          const unformattedValue = e.target.value.replace(/-/g, "");
          formik.setFieldValue(e.target.name, unformattedValue); // update without dashes
          formik.handleBlur(e);
          
          // Also update the parent component if onChange exists
          if (onChange) {
            const updatedValues = {
              ...formik.values,
              [e.target.name]: unformattedValue,
            };
            onChange(updatedValues);
          }
        }}
      >
        {(inputProps) => (
          <Input
            {...inputProps}
            name={field.inputName || field.id}
            id={field.inputName || field.id}
            placeholder={field.placeholder || "Enter phone number"}
            invalid={
              formik.touched[field.inputName || field.id] &&
              Boolean(formik.errors[field.inputName || field.id])
            }
            onKeyDown={(e) => {
              if (
                e.key.toLowerCase() === "e" ||
                e.key === "+" ||
                e.key === "-" ||
                isNaN(Number(e.key)) && e.key !== "Backspace" && e.key !== "Tab" && e.key !== "ArrowLeft" && e.key !== "ArrowRight"
              ) {
                e.preventDefault();
              }
            }}
          />
        )}
      </InputMask>
      ) : field.label === "Vehicle Number" || field.id === "vehicleNumber" ? (
        <InputMask
          // mask="aa-99-aa-9999" // Vehicle number format (adapt as needed)
          maskChar={null}
          disabled={disabled}
          value={formik.values[field.inputName || field.id]}
          onChange={(e) => {
            formik.handleChange(e);
            if (onChange) {
              const updatedValues = {
                ...formik.values,
                [e.target.name]: e.target.value,
              };
              onChange(updatedValues);
            }
          }}
          onBlur={(e) => {
            formik.setFieldValue(e.target.name, e.target.value.replace(/-/g, "")); // Removes dashes on blur
            formik.handleBlur(e);
          }}
        >
          {(inputProps) => (
            <Input
              {...inputProps}
              type="text"
              id={field.inputName || field.id}
              name={field.inputName || field.id}
              placeholder={field.placeholder}
              maxLength={field.maxlength}
              invalid={
                formik.touched[field.inputName || field.id] &&
                Boolean(formik.errors[field.inputName || field.id])
              }
              style={{ textTransform: "uppercase" }}
            />
          )}
        </InputMask>
      ) : field.type === "email" ? (
        <Input
          disabled={isEditMode || disabled}
          type={field.type}
          id={field.inputName}
          name={field.inputName}
          value={formik.values[field.inputName]}
          onChange={(e) => {
            formik.handleChange(e);
            if (onChange) {
              const updatedValues = {
                ...formik.values,
                [e.target.name]: e.target.value,
              };
              onChange(updatedValues);
            }
          }}
          onBlur={formik.handleBlur}
          invalid={
            formik.touched[field.inputName] &&
            Boolean(formik.errors[field.inputName])
          }
          placeholder={field.placeholder}
        />
      ) :field.type === "date" ? ( 
        <Input
          disabled={disabled}
          type={field.type}
          id={field.id}
          name={field.id}
          value={getFormattedDate(formik.values[field.inputName])}
          onChange={(e) => {
            // If there's an existing time, use it
            const existingTime = formik.values[field.inputName]
              ? getFormattedTime(formik.values[field.inputName])
              : "00:00";
      
            // Combine the new date with existing time
            const combinedDateTime = new Date(`${e.target.value}T${existingTime}`);
            const timestamp = combinedDateTime.getTime(); // Convert to timestamp
      
            formik.setFieldValue(field.inputName, timestamp);
            if (onChange) onChange({ ...formik.values, [field.inputName]: timestamp });
          }}
        />
      ) : field.type === "time" ? (
        <Input
          disabled={disabled}
          type="time"
          id={field.inputName}
          name={field.inputName}
          value={getFormattedTime(formik.values[field.inputName])}
          onChange={(e) => {
            // If there's an existing date, use it
            const existingDate = formik.values[field.inputName]
              ? getFormattedDate(formik.values[field.inputName])
              : new Date().toISOString().split('T')[0]; // Default to today
      
            // Parse the selected time
            const [hours, minutes] = e.target.value.split(':');
      
            // Create a new date using the existing date and selected time
            const combinedDateTime = new Date(existingDate);
            combinedDateTime.setHours(Number(hours), Number(minutes), 0, 0);
      
            const timestamp = combinedDateTime.getTime(); // Convert to timestamp
      
            formik.setFieldValue(field.inputName, timestamp);
            if (onChange) onChange({ ...formik.values, [field.inputName]: timestamp });
          }}
        />
      ) 
       :
      (
        <Input
          disabled={disabled}
          type={field.type}
          id={field.inputName}
          name={field.inputName}
          placeholder={field.placeholder}
          value={formik.values[field.inputName]}
          onChange={(e) => {
            formik.handleChange(e);
            if (onChange) {
              const updatedValues = {
                ...formik.values,
                [e.target.name]: e.target.value,
              };
              onChange(updatedValues);
            }
          }}
          onBlur={(e) => {
            // Only remove dashes if it's a field that should have them removed
            const shouldRemoveDashes = field.removeDashesOnBlur === true;
            if (shouldRemoveDashes) {
              formik.setFieldValue(e.target.name, e.target.value.replace(/-/g, ""));
            }
            formik.handleBlur(e);
          }}
          invalid={
            formik.touched[field.inputName] &&
            Boolean(formik.errors[field.inputName])
          }
        />
      )}
      {formik.touched[field.inputName || field.id] && formik.errors[field.inputName || field.id] && (
        <FormFeedback>{formik.errors[field.inputName || field.id]}</FormFeedback>
      )}
    </FormGroup>
  ));
};

export default generateFormUI;